/**
 * Local Storage Adapter for Anonymous (Pre-Registration) Users
 *
 * Stores individual work responses in localStorage so users can
 * complete the questionnaire before creating an account.
 * On registration, responses are migrated to Firestore via migrate-local-responses.ts.
 *
 * Uses the same QuestionResponse / UserResponses shape as storage.ts
 * to make migration seamless.
 */

import type { QuestionResponse, UserResponses, SectionProgress } from './storage';

const STORAGE_KEY = 'tlc_anon_responses';
const SESSION_KEY = 'tlc_anon_session';
const COMFORT_KEY = 'tlc_anon_comfort_level';
const PRIVATE_Q_KEY = 'tlc_anon_private_questions';
const PRIVATE_S_KEY = 'tlc_anon_private_sections';
const INVITE_TOKEN_KEY = 'tlc_anon_invite_token';

// ============= SESSION =============

export interface AnonSession {
  sessionId: string;
  createdAt: string;
  comfortLevel: number;
  onboardingCompleted: boolean;
  inviteToken?: string; // If user arrived via /join/:token
}

/**
 * Get or create an anonymous session (UUID-based).
 */
export function getOrCreateAnonSession(): AnonSession {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.sessionId) return parsed;
    }
  } catch {
    // Corrupted data — recreate
  }

  const session: AnonSession = {
    sessionId: `anon_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    createdAt: new Date().toISOString(),
    comfortLevel: 2, // Default: Open
    onboardingCompleted: false,
  };

  // Restore invite token if present
  const inviteToken = localStorage.getItem(INVITE_TOKEN_KEY);
  if (inviteToken) {
    session.inviteToken = inviteToken;
  }

  saveAnonSession(session);
  return session;
}

export function getAnonSession(): AnonSession | null {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.sessionId) return parsed;
    }
  } catch {
    // Corrupted
  }
  return null;
}

export function saveAnonSession(session: AnonSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    console.warn('[AnonStorage] Failed to save session to localStorage');
  }
}

export function hasAnonSession(): boolean {
  return getAnonSession() !== null;
}

// ============= COMFORT LEVEL =============

export function setAnonComfortLevel(level: number): void {
  const session = getOrCreateAnonSession();
  session.comfortLevel = level;
  saveAnonSession(session);
}

export function getAnonComfortLevel(): number {
  const session = getAnonSession();
  return session?.comfortLevel ?? 2;
}

// ============= ONBOARDING =============

export function markAnonOnboardingComplete(): void {
  const session = getOrCreateAnonSession();
  session.onboardingCompleted = true;
  saveAnonSession(session);
}

export function isAnonOnboardingComplete(): boolean {
  const session = getAnonSession();
  return session?.onboardingCompleted ?? false;
}

// ============= INVITE TOKEN =============

export function setAnonInviteToken(token: string): void {
  localStorage.setItem(INVITE_TOKEN_KEY, token);
  const session = getAnonSession();
  if (session) {
    session.inviteToken = token;
    saveAnonSession(session);
  }
}

export function getAnonInviteToken(): string | null {
  return localStorage.getItem(INVITE_TOKEN_KEY);
}

// ============= RESPONSES =============

function loadResponses(): Record<string, QuestionResponse> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // Corrupted
  }
  return {};
}

function persistResponses(responses: Record<string, QuestionResponse>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(responses));
  } catch (e) {
    console.error('[AnonStorage] Failed to persist responses:', e);
  }
}

/**
 * Save a single response (mirrors storage.ts saveResponse signature minus Firestore deps).
 */
export function saveAnonResponse(
  questionId: string,
  value: string | string[],
  isPrivate: boolean = false
): void {
  const responses = loadResponses();
  responses[questionId] = {
    questionId,
    value,
    updatedAt: new Date().toISOString(),
    isPrivate,
    sharedWithPartner: !isPrivate,
    encrypted: false,
  };
  persistResponses(responses);
}

/**
 * Get all responses in the UserResponses shape expected by the rest of the app.
 */
export function getAnonUserResponses(): UserResponses {
  const session = getAnonSession();
  const responses = loadResponses();

  return {
    userId: session?.sessionId ?? 'anonymous',
    createdAt: session?.createdAt ?? new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    responses,
    progress: calculateAnonProgress(responses),
    relationshipId: null,
    privateSections: getAnonPrivateSections(),
    onboardingCompleted: session?.onboardingCompleted ?? false,
  };
}

/**
 * Get a single response value.
 */
export function getAnonResponse(questionId: string): QuestionResponse | null {
  const responses = loadResponses();
  return responses[questionId] ?? null;
}

// ============= PRIVACY =============

export function toggleAnonQuestionPrivacy(questionId: string, isPrivate: boolean): void {
  // Update the response's privacy flag
  const responses = loadResponses();
  if (responses[questionId]) {
    responses[questionId].isPrivate = isPrivate;
    responses[questionId].sharedWithPartner = !isPrivate;
    persistResponses(responses);
  }

  // Also track in the dedicated set
  const privateQs = getAnonPrivateQuestions();
  if (isPrivate) {
    privateQs.add(questionId);
  } else {
    privateQs.delete(questionId);
  }
  try {
    localStorage.setItem(PRIVATE_Q_KEY, JSON.stringify([...privateQs]));
  } catch { /* ignore */ }
}

export function getAnonPrivateQuestions(): Set<string> {
  try {
    const stored = localStorage.getItem(PRIVATE_Q_KEY);
    if (stored) return new Set(JSON.parse(stored));
  } catch { /* ignore */ }
  return new Set();
}

export function toggleAnonSectionPrivacy(sectionId: string, isPrivate: boolean): void {
  const sections = getAnonPrivateSections();
  const set = new Set(sections);
  if (isPrivate) {
    set.add(sectionId);
  } else {
    set.delete(sectionId);
  }
  try {
    localStorage.setItem(PRIVATE_S_KEY, JSON.stringify([...set]));
  } catch { /* ignore */ }
}

export function getAnonPrivateSections(): string[] {
  try {
    const stored = localStorage.getItem(PRIVATE_S_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return [];
}

export function getAnonPrivacySettings(): { questions: string[]; sections: string[] } {
  return {
    questions: [...getAnonPrivateQuestions()],
    sections: getAnonPrivateSections(),
  };
}

// ============= PROGRESS =============

function calculateAnonProgress(
  responses: Record<string, QuestionResponse>
): Record<string, SectionProgress> {
  // Simple progress: count answered questions per topic
  // The actual topic grouping happens in the UI layer (IndividualWork.tsx)
  // Here we just provide a flat progress record
  const progress: Record<string, SectionProgress> = {};
  const answered = Object.keys(responses).length;

  progress['overall'] = {
    sectionId: 'overall',
    totalQuestions: 0, // Will be set by the UI when questions are loaded
    answeredQuestions: answered,
    completed: false,
    lastUpdated: new Date().toISOString(),
  };

  return progress;
}

export function getAnonOverallProgress(totalQuestions: number): {
  total: number;
  answered: number;
  percentage: number;
} {
  const responses = loadResponses();
  const privateSections = getAnonPrivateSections();
  const privateQuestions = getAnonPrivateQuestions();
  const answered = Object.keys(responses).length + privateQuestions.size;

  return {
    total: totalQuestions,
    answered,
    percentage: totalQuestions > 0 ? Math.round((answered / totalQuestions) * 100) : 0,
  };
}

// ============= CLEANUP =============

/**
 * Clear all anonymous data (called after successful migration to Firestore).
 */
export function clearAnonData(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(COMFORT_KEY);
    localStorage.removeItem(PRIVATE_Q_KEY);
    localStorage.removeItem(PRIVATE_S_KEY);
    localStorage.removeItem(INVITE_TOKEN_KEY);
  } catch {
    // Best effort
  }
}

/**
 * Check if there are anonymous responses to migrate.
 */
export function hasAnonResponses(): boolean {
  const responses = loadResponses();
  return Object.keys(responses).length > 0;
}

/**
 * Get raw responses for migration.
 */
export function getAnonResponsesForMigration(): {
  responses: Record<string, QuestionResponse>;
  privateSections: string[];
  privateQuestions: string[];
  comfortLevel: number;
  inviteToken: string | null;
} {
  return {
    responses: loadResponses(),
    privateSections: getAnonPrivateSections(),
    privateQuestions: [...getAnonPrivateQuestions()],
    comfortLevel: getAnonComfortLevel(),
    inviteToken: getAnonInviteToken(),
  };
}
